import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown } from "primereact/dropdown";

import UiButton from "../button/Button";
import ValueEditor, { FieldOption } from "./ValueEditor";
import {
	ConditionOperator,
	ConditionRule,
	ConditionValue,
	isDateLikeType,
	operatorTakesValue,
	operatorsForFieldType,
} from "../../utils/conditions";

interface ConditionRuleEditorProps {
	rule: ConditionRule;
	onChange: (rule: ConditionRule) => void;
	onRemove: () => void;
	fields: FieldOption[];
}

const defaultValueForType = (fieldType?: string): ConditionValue =>
	isDateLikeType(fieldType) ? { kind: "datetime", iso: "" } : { kind: "literal", value: "" };

/**
 * One comparison row: field → operator → value. The operator list and the
 * default value kind adapt to the selected field's type; existence operators
 * (`?` / `!?`) hide the value editor entirely.
 */
const ConditionRuleEditor = ({ rule, onChange, onRemove, fields }: ConditionRuleEditorProps) => {
	const { t } = useTranslation("common");

	const fieldType = useMemo(
		() => fields.find((field) => field.name === rule.fieldName)?.type,
		[fields, rule.fieldName]
	);

	const allowedOperators = operatorsForFieldType(fieldType);

	const handleFieldChange = (name: string) => {
		const nextType = fields.find((field) => field.name === name)?.type;
		const nextAllowed = operatorsForFieldType(nextType);
		const operator = nextAllowed.includes(rule.operator) ? rule.operator : nextAllowed[0];
		const value = operatorTakesValue(operator) ? defaultValueForType(nextType) : undefined;
		onChange({ ...rule, fieldName: name, operator, value });
	};

	const handleOperatorChange = (operator: ConditionOperator) => {
		const value = operatorTakesValue(operator)
			? (rule.value ?? defaultValueForType(fieldType))
			: undefined;
		onChange({ ...rule, operator, value });
	};

	const fieldOptions = fields.map((field) => ({
		label: field.type ? `${field.name} (${field.type})` : field.name,
		value: field.name,
	}));

	return (
		<div className="cb-rule">
			<Dropdown
				className="cb-field"
				value={rule.fieldName}
				options={fieldOptions}
				editable
				filter
				placeholder={t("MARKETING_CAMPAIGNS.CONDITIONS.FIELD_PLACEHOLDER")}
				onChange={(event) => handleFieldChange(event.value)}
			/>

			<Dropdown
				className="cb-operator"
				value={rule.operator}
				options={allowedOperators.map((operator) => ({
					label: t(`MARKETING_CAMPAIGNS.CONDITIONS.OPERATOR.${operator}`),
					value: operator,
				}))}
				onChange={(event) => handleOperatorChange(event.value)}
			/>

			{operatorTakesValue(rule.operator) && (
				<ValueEditor
					value={rule.value}
					onChange={(value) => onChange({ ...rule, value })}
					fieldType={fieldType}
					fields={fields}
				/>
			)}

			<UiButton
				type="button"
				className="p-button-text p-button-sm neutral cb-remove"
				icon="pi pi-trash"
				tooltip="MARKETING_CAMPAIGNS.CONDITIONS.REMOVE_RULE"
				onClick={onRemove}
			/>
		</div>
	);
};

export default ConditionRuleEditor;
